/**
 * キーボードショートカット共通ユーティリティ。
 *
 * - 判定系（isTypingTarget / getAltDigit）: GlobalShortcutListener（⌥1/⌥2）と
 *   useProjectShortcuts（⌥3〜⌥9 / Esc）で共有する。
 * - アクション系（dispatchShortcutAction / useShortcutAction）: 「既に対象ページを
 *   表示中の再押下」を集中リスナーから各ページへ伝えるための橋渡し。
 */

import { useEffect, useRef } from "react";

/**
 * macOS 系（iPadOS / iOS 含む）か。修飾キーの表記を ⌘ / Ctrl で出し分けるために使う。
 * 判定に失敗しても ⌘ / Ctrl は両方受け付けるので、表記が揺れるだけで実害はない。
 */
export const isMacLike =
  typeof navigator !== "undefined" &&
  /Mac|iPhone|iPad|iPod/i.test(navigator.platform ?? "");

/** 入力系要素（INPUT / TEXTAREA / contentEditable）にフォーカスがあるか */
export const isTypingTarget = (target: EventTarget | null): boolean => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return (
    el.tagName === "INPUT" ||
    el.tagName === "TEXTAREA" ||
    !!el.isContentEditable
  );
};

/**
 * Option(Alt) のみを修飾キーとした数字キー（0〜9）の押下なら、その数字を返す。
 * それ以外は null。
 *
 * macOS では Option+数字が特殊文字を入力するため `e.key` は使えず、
 * 物理キー位置ベースの `e.code`（Digit0〜9 / Numpad0〜9）で判定する。
 */
export const getAltDigit = (e: KeyboardEvent): number | null => {
  if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return null;
  if (e.repeat || e.isComposing) return null;
  const match = /^(?:Digit|Numpad)([0-9])$/.exec(e.code);
  return match ? Number(match[1]) : null;
};

/**
 * プロジェクト移動（前 / 次）の生成文字（e.key）による判定。
 *
 * Windows / Linux は Alt 併用でも刻印どおりの文字が出るため、これだけで足りる。
 * かな入力や全角での表記ゆれも同じ意味として受ける。
 *
 * macOS は Option 併用で別の文字（“ ‘ « 等）になり、しかもその文字が
 * **キー配列によってずれる**ため、角括弧については当てにできない。macOS の
 * 角括弧は物理キー位置（DIRECTION_BY_CODE）で判定する。カンマ / ピリオドは
 * macOS でも ≤ / ≥ で一意に決まるのでここで受ける。
 */
const directionByKey = (key: string): -1 | 1 | null => {
  if (key === "[" || key === "［" || key === "「") return -1;
  if (key === "]" || key === "］" || key === "」") return 1;
  if (key === "," || key === "，" || key === "、" || key === "\u2264")
    return -1;
  if (key === "." || key === "．" || key === "。" || key === "\u2265") return 1;
  return null;
};

/**
 * プロジェクト移動の物理キー位置（e.code）による判定。macOS 用の本命。
 *
 * 角括弧の物理位置はキー配列で異なる:
 * - US 配列:  `[` = BracketLeft / `]` = BracketRight
 * - JIS 配列: `[` = BracketRight / `]` = Backslash（BracketLeft は `@`）
 *
 * つまり `BracketRight` は **JIS で `[`（前へ）、US で `]`（次へ）と意味が正反対**。
 * 加えて macOS では両者の Option 文字がどちらも `‘` になるため、e.key でも
 * 区別できない。配列を特定できる唯一の手段だった Keyboard Layout Map API は、
 * macOS の Chrome では記号キーを 1 つも返さない（entries が空）ことを実測で
 * 確認済みで、当てにできない。
 *
 * そのため **JIS 配列を優先**すると決めた。US 配列では ⌥] が「前へ」に倒れる
 * 副作用があるため、配列に依存しない ⌥, / ⌥. を併用できるようにしてある。
 */
const DIRECTION_BY_CODE: Record<string, -1 | 1> = {
  // US の `[`。JIS では `@` だが ⌥@ に用途は無い
  BracketLeft: -1,
  // JIS の `[`。US では `]` にあたるが、区別できないため JIS を優先する
  BracketRight: -1,
  // JIS の `]`。US では `\` だが ⌥\ に用途は無い
  Backslash: 1,
  // US / JIS で位置が変わらない。配列非依存の代替キー
  Comma: -1,
  Period: 1,
};

/**
 * Option(Alt) のみを修飾キーとしたプロジェクト移動キーの押下なら、
 * 前へ = -1、次へ = +1 を返す。それ以外は null。
 *
 * 対象は ⌥[ / ⌥]（JIS 配列基準）と、その配列非依存エイリアスの ⌥, / ⌥.。
 * 生成文字（e.key）で判定できればそれを使い、macOS のように Option で文字が
 * 変わる環境は物理キー位置（e.code）へ落とす。
 */
export const getAltProjectDirection = (e: KeyboardEvent): -1 | 1 | null => {
  if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return null;
  if (e.repeat || e.isComposing) return null;
  return directionByKey(e.key) ?? DIRECTION_BY_CODE[e.code] ?? null;
};

/**
 * Option(Alt) のみを修飾キーとした左右矢印キーの押下なら、
 * 左 = -1、右 = +1 を返す。それ以外は null。
 *
 * タスクボードのタブ（ボード）切り替え ⌥←/→ に使う。矢印キーは
 * キー配列の影響を受けないため e.key だけで判定できる。
 * macOS 既定の ⌥←/→ は「単語単位のカーソル移動」だが、それは入力中のみの
 * 挙動なので、呼び出し側が isTypingTarget で入力中を除外すれば衝突しない。
 */
export const getAltArrowDirection = (e: KeyboardEvent): -1 | 1 | null => {
  if (!e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return null;
  if (e.repeat || e.isComposing) return null;
  if (e.key === "ArrowLeft") return -1;
  if (e.key === "ArrowRight") return 1;
  return null;
};

/**
 * 「既に対象ページに居る状態での再押下」でページ内の状態を循環切替するための
 * アクション。遷移判定を持つ集中リスナー（GlobalShortcutListener /
 * useProjectShortcuts）が dispatch し、切替対象の state を持つ各ページが
 * useShortcutAction で購読する。
 *
 * - taskboard-cycle: ⌥1 再押下 … タスク → スケジュール → 工数 の循環
 * - projects-next:   ⌥2 再押下 … ホイールの選択を次のプロジェクトへ
 * - home-cycle:      ⌥3 再押下 … 進行中 → 完了 → ショットリスト
 * - talkroom-cycle:  ⌥4 再押下 … 進行中 → 完了 → ファイルリスト
 * - dm-cycle:        ⌥5 再押下 … 進行中 ↔ 完了
 * - config-cycle:    ⌥6 再押下 … 設定メニューを定義順に循環
 */
export type ShortcutAction =
  | "taskboard-cycle"
  | "projects-next"
  | "home-cycle"
  | "talkroom-cycle"
  | "dm-cycle"
  | "config-cycle";

const SHORTCUT_ACTION_EVENT = "soremo:shortcut-action";

/** ページ内アクションを発火する（集中リスナー側） */
export const dispatchShortcutAction = (action: ShortcutAction): void => {
  window.dispatchEvent(
    new CustomEvent<{ action: ShortcutAction }>(SHORTCUT_ACTION_EVENT, {
      detail: { action },
    }),
  );
};

/**
 * dispatchShortcutAction で発火されたアクションを購読する（ページ側）。
 * handler は ref 経由で常に最新を参照するため、依存配列を気にせず
 * レンダー内の値をそのまま読める。
 */
export const useShortcutAction = (
  action: ShortcutAction,
  handler: () => void,
): void => {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  });

  useEffect(() => {
    const listener = (e: Event) => {
      const detail = (e as CustomEvent<{ action?: ShortcutAction }>).detail;
      if (detail?.action === action) handlerRef.current();
    };
    window.addEventListener(SHORTCUT_ACTION_EVENT, listener);
    return () => window.removeEventListener(SHORTCUT_ACTION_EVENT, listener);
  }, [action]);
};
